import { CommonModule } from "@angular/common";
import { Component, ElementRef, Inject, OnInit } from "@angular/core";
import {
  FormGroup,
  NonNullableFormBuilder,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { MsalService } from "@azure/msal-angular";
import { AlertService } from "../b2b/ops/shared/services/alert.service";
import {
  ValidationProblemDetails,
  IBackendClient,
  RegistrationRequest,
  ProblemDetails,
  BadRequestResponse,
} from "../services/BackendClient";
import { BackendClientProvider } from "../services/BackendClientProvider";
import { PasswordValidation } from "../shared/enums/password-validation";
import { IRegisterForm, RegisterControls as controls } from "../shared/typed-forms/register-form";
import { passwordValidator } from "../shared/controls/password-validator";
import { first, finalize, Observable, throwError, catchError, tap } from "rxjs";
import { OpsLoadingComponent } from "../b2b/ops/shared/components/ops-loading/ops-loading.component";
import { Router } from "@angular/router";
import { RoutingNames } from "../shared/enums/routes-names";

@Component({
  selector: "app-chainalysis-registration",
  templateUrl: "./chainalysis.component.html",
  styleUrls: ["./chainalysis.component.scss"],
  imports: [CommonModule, ReactiveFormsModule, OpsLoadingComponent],
})
export class ChainalysisComponent implements OnInit {
  public registerForm: FormGroup<IRegisterForm>;
  public readonly controls = controls;
  public readonly passwordValidation = PasswordValidation;
  public readonly minPasswordLength: number;
  public readonly maxPasswordLength: number;
  public readonly btnRegister: string;
  public isPasswordVisible: boolean;
  public isPassword2Visible: boolean;
  public isEmailVerification: boolean;
  public isLoading: boolean;
  public validationDetails: ValidationProblemDetails;

  public get isRegisterBtnDisabled(): boolean {
    return !this.registerForm.valid || this.isLoading;
  }

  public get isPasswordPristine(): boolean {
    return this.registerForm.controls.Password.pristine;
  }

  public get isPassword2Pristine(): boolean {
    return this.registerForm.controls.ConfirmPassword.pristine;
  }

  public get isDisabled(): boolean {
    return this.isLoading || this.registerForm.invalid || this.passwordsAreDifferent;
  }

  public get passwordsAreDifferent(): boolean {
    const { Password, ConfirmPassword } = this.registerForm.value;
    return Password !== ConfirmPassword;
  }

  public constructor(
    public msal: MsalService,
    private alertService: AlertService,
    private host: ElementRef<HTMLElement>,
    private router: Router,
    private nonNullableBuilder: NonNullableFormBuilder,
    @Inject(BackendClientProvider) private backendClient: IBackendClient,
  ) {
    this.host.nativeElement.classList.add("fadeIn");
    this.btnRegister = $localize`Register`;
    this.isLoading = false;
    this.isPasswordVisible = false;
    this.isEmailVerification = false;
    this.minPasswordLength = 8;
    this.maxPasswordLength = 64;
  }

  public ngOnInit(): void {
    this.initForm();
  }

  public register(): void {
    this.isLoading = true;
    this.validationDetails = null;
    const form = this.registerForm.value;
    const request = new RegistrationRequest({
      email: form.Email,
      password: form.Password,
      firstName: form.Firstname,
      lastName: form.Lastname,
    });

    this.backendClient
      .registerChainalysisUser(request)
      .pipe(
        first(),
        tap(() => this.onRegisterSuccess()),
        catchError((error) => this.onRegisterError(error)),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();
  }

  private redirectToWatchlist(): void {
    void this.router.navigate([
      RoutingNames.B2B,
      RoutingNames.B2B_CLIENT_WATCHLIST,
      RoutingNames.B2B_OPS_INVESTIGATE,
    ]);
  }

  private onRegisterSuccess(): void {
    this.redirectToWatchlist();
  }

  private onRegisterError(error: unknown): Observable<never> {
    if (error instanceof ValidationProblemDetails) {
      this.validationDetails = error;
    } else if (error instanceof ProblemDetails && error.status === 409) {
      this.alertService.error("This email address is already registered. Please try to login.");
    } else if (error instanceof BadRequestResponse && error.status === 400) {
      const message = "The form seems to be invalid. We could not create your user.";
      this.alertService.error(message);
    } else if (error instanceof Error) {
      const message = "There was a problem creating your account";
      this.alertService.error(message);
    }
    return throwError(() => error);
  }

  private initForm(): void {
    this.registerForm = this.nonNullableBuilder.group<IRegisterForm>({
      [controls.Firstname]: new FormControl<string>("", [Validators.required]),
      [controls.Lastname]: new FormControl<string>("", [Validators.required]),
      [controls.Email]: new FormControl<string>("", [Validators.required, Validators.email]),
      [controls.Password]: new FormControl<string>("", [
        Validators.required,
        Validators.minLength(this.minPasswordLength),
        Validators.maxLength(this.maxPasswordLength),
        passwordValidator(),
      ]),
      [controls.ConfirmPassword]: new FormControl<string>("", [
        Validators.required,
        Validators.minLength(this.minPasswordLength),
        Validators.maxLength(this.maxPasswordLength),
        passwordValidator(),
      ]),
    });
  }

  public hasErrors(key: controls): boolean {
    const control = this.registerForm.get(key);
    return control.errors && control.dirty;
  }

  public hasRequirementError(error: PasswordValidation): boolean {
    const control = this.registerForm.controls.Password;
    return control.dirty && control.errors && !!control.errors[error];
  }

  public hasNotMetRequirement(requirement: PasswordValidation): boolean {
    const control = this.registerForm.controls.Password;
    return control.dirty && control["requirement"] === requirement;
  }
}
